import type { UpdateContactInput } from 'app/generated/gql/graphql'
import { withZod } from '@remix-validated-form/with-zod'
import classNames from 'classnames'
import moment from 'moment'
import { ReactElement, useState } from 'react'
import { useFormContext } from 'remix-validated-form'
import { z } from 'zod'
import Button from '../Button'
import FormDatePicker from '../form/FormDatePicker'
import FormInput from '../form/FormInput'
import FormSelect, { type FormComboSelectChange } from '../form/FormComboSelect'
import { SmartForm } from '../form/SmartForm'
import FormHiddenInput from 'app/components/form/FormHiddenInput'
import type { ContactJson } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { debug, logError } from 'lib/logging'
import { minDate } from 'lib/minDate'

type ContactData = {
  id: string
  lifecycleStage: string
  leadStatus: string
  becamePatientDate?: string | Date | undefined
  productionLtv?: string | number | undefined
  scheduledProduction?: string | number | undefined
  contactScheduledDate?: string | Date | undefined
}

// Lifecycle options when editing from Patient status (TRK-3874)
const LIFECYCLE_OPTIONS_FROM_PATIENT = [
  { label: `Patient`, value: `Patient` },
  { label: `Qualified Lead`, value: `Qualified Lead` },
  { label: `New Patient Scheduled`, value: `New Patient Scheduled` },
]

// Lead Status options based on lifecycle selection (TRK-3874)
const LEAD_STATUS_OPTIONS_QUALIFIED_LEAD = [
  { label: `In Progress`, value: `In Progress` },
  { label: `Needs Action`, value: `Needs Action` },
]

const LEAD_STATUS_OPTIONS_NEW_PATIENT_SCHEDULED = [
  { label: `Scheduled`, value: `Scheduled` },
  { label: `In Progress`, value: `In Progress` },
  { label: `Needs Action`, value: `Needs Action` },
]

const LEAD_STATUS_OPTIONS_PATIENT = [{ label: `Closed Won`, value: `Closed Won` }]

const handleFormatCurrency = (e: React.ChangeEvent<HTMLInputElement>) => {
  const value = e.target.value.replace(/[^0-9.]/g, ``)
  e.target.value = Number(value).toLocaleString(`en-US`, { style: `currency`, currency: `USD` })
}

const markPatientValidator = withZod(
  z
    .object({
      id: z.string().min(1, { message: `Contact Id required` }),
      patientStatus: z.string().optional(),
      lifecycleStage: z.string().min(1, { message: `Lifecycle Stage required` }),
      leadStatus: z.string().min(1, { message: `Lead Status required` }),
      becamePatientDate: z
        .string()
        .optional()
        .transform((value?: string) => (value ? moment(value, `YYYY-MM-DD`).toDate() : undefined)),
      firstAppointmentMadeDate: z
        .string()
        .optional()
        .transform((value?: string) => (value ? moment(value, `YYYY-MM-DD`).toDate() : undefined)),
      becameLeadDate: z
        .string()
        .optional()
        .transform((value?: string) => (value ? moment(value, `YYYY-MM-DD`).toDate() : undefined)),
      productionLtv: z
        .string()
        .optional()
        .transform((value?: string) => (value ? Number(value?.replace(/[^0-9.-]+/g, ``)) : undefined)),
      scheduledProduction: z
        .string()
        .optional()
        .transform((value?: string) => (value ? Number(value?.replace(/[^0-9.-]+/g, ``)) : undefined)),
      contactScheduledDate: z
        .string()
        .optional()
        .transform((value?: string) => (value ? moment(value, `YYYY-MM-DD`).toDate() : undefined)),
    })
    .superRefine((data, ctx) => {
      if (data.lifecycleStage === `Patient` && !data.becamePatientDate) {
        ctx.addIssue({ code: `custom`, message: `Became a patient Date required`, path: [`becamePatientDate`] })
      }
      if (
        data.lifecycleStage === `New Patient Scheduled` &&
        data.leadStatus === `Scheduled` &&
        !data.contactScheduledDate
      ) {
        ctx.addIssue({ code: `custom`, message: `Contact Scheduled Date required`, path: [`contactScheduledDate`] })
      }
    }),
)

const UpdateContact = ({
  contact,
  handleMarkPatient,
  markPatient,
}: {
  contact: ContactJson
  handleMarkPatient: (contact: UpdateContactInput) => void
  markPatient?: boolean
}) => {
  const formContext = useFormContext(`updatePatient`)
  const [loading, setLoading] = useState<boolean>(false)
  const [selectedLifecycle, setSelectedLifecycle] = useState<string>(
    markPatient ? `Patient` : contact.lifecycleStage || `Patient`,
  )
  const [selectedLeadStatus, setSelectedLeadStatus] = useState<string>(
    markPatient
      ? `Closed Won`
      : contact.lifecycleStage === `Patient`
        ? `Closed Won`
        : contact.leadStatus || `In Progress`,
  )

  const isPatientLifecycle = selectedLifecycle === `Patient`
  // const isQualifiedLead = selectedLifecycle === `Qualified Lead`
  const isNewPatientScheduled = selectedLifecycle === `New Patient Scheduled`
  const showScheduledDate = isNewPatientScheduled && selectedLeadStatus === `Scheduled`

  const getLeadStatusOptions = () => {
    switch (selectedLifecycle) {
      case `Qualified Lead`:
        return LEAD_STATUS_OPTIONS_QUALIFIED_LEAD
      case `New Patient Scheduled`:
        return LEAD_STATUS_OPTIONS_NEW_PATIENT_SCHEDULED
      default:
        return LEAD_STATUS_OPTIONS_PATIENT
    }
  }

  const handleLifecycleChange = (e: FormComboSelectChange) => {
    const newLifecycle = e.target.value
    setSelectedLifecycle(newLifecycle)

    if (newLifecycle === `Patient`) {
      setSelectedLeadStatus(`Closed Won`)
    } else if (newLifecycle === `Qualified Lead`) {
      setSelectedLeadStatus(`In Progress`)
    } else if (newLifecycle === `New Patient Scheduled`) {
      setSelectedLeadStatus(`Scheduled`)
    }
  }

  const handleLeadStatusChange = (e: FormComboSelectChange) => {
    setSelectedLeadStatus(e.target.value)
  }

  let contactData: ContactData = {
    id: contact.id!,
    lifecycleStage: selectedLifecycle,
    leadStatus: selectedLeadStatus,
    becamePatientDate: contact.becamePatientDate ? moment(contact.becamePatientDate).format(`YYYY-MM-DD`) : undefined,
    productionLtv: Number(contact.productionLtv ?? 0).toLocaleString(`en-US`, { style: `currency`, currency: `USD` }),
    scheduledProduction: Number(contact.scheduledProduction ?? 0).toLocaleString(`en-US`, {
      style: `currency`,
      currency: `USD`,
    }),
  }

  const handleSubmitForm = async () => {
    setLoading(true)
    let form = document.getElementById(`updatePatient`) as HTMLFormElement
    let data = Object.fromEntries(new FormData(form).entries())
    let result = await markPatientValidator.validate(data)
    debug(`contacts`, `actions - markPatient`, result)
    if (result.error || !result.data) {
      setLoading(false)
      // Separate the two failure cases so logs show which one happened: a
      // validation error carries field messages; a missing result.data means
      // the validator produced no output without an error object.
      if (result.error) {
        logError(`contacts`, `actions - markPatient validation error`, result.error)
      } else {
        logError(`contacts`, `actions - markPatient produced no data`)
      }
      // Set field errors on form context so each field displays its own error
      await formContext.validate()
      return
    }

    const { id: _id, ...fields } = result.data
    const payload: Omit<UpdateContactInput, 'becamePatientDate' | 'becameLeadDate' | 'firstAppointmentMadeDate'> & {
      becamePatientDate?: Date | null
      becameLeadDate?: Date | null
      firstAppointmentMadeDate?: Date | null
      contactScheduledDate?: Date
    } = { ...fields }

    if (payload.lifecycleStage === `Patient`) {
      payload.patientStatus = `Active`
      payload.firstAppointmentMadeDate = minDate(
        payload.becamePatientDate,
        payload.firstAppointmentMadeDate,
        contact.firstAppointmentMadeDate,
      )
      payload.becameLeadDate = minDate(
        payload.becamePatientDate,
        payload.firstAppointmentMadeDate,
        contact.becameLeadDate,
      )
    } else {
      /**
       * TRK-3874: When changing lifecycle from Patient to Qualified Lead or New Patient Scheduled,
       * we must clear BPD (becamePatientDate) otherwise calculateLifeCycle() will revert
       * the lifecycle back to Patient since it checks: if (becamePatientDate) → Patient
       */
      payload.becamePatientDate = null
      payload.productionLtv = null
      payload.scheduledProduction = null
      payload.patientStatus = undefined

      if (payload.lifecycleStage === `New Patient Scheduled` && payload.leadStatus === `Scheduled`) {
        payload.firstAppointmentMadeDate = payload.contactScheduledDate
        /**
         * TRK-3874 QUESTION: Should appointmentStatus also be set to Scheduled here?
         * Currently only setting leadStatus per requirements, but appointmentStatus
         * may need to be aligned for consistency.
         */
      } else {
        /**
         * TRK-3874 QUESTION: If lifecycle is New Patient Scheduled but leadStatus is not Scheduled,
         * should we still set BNPS (firstAppointmentMadeDate) to keep lifecycle from reverting?
         * Currently clearing BNPS per requirements, but this means lifecycle may revert to
         * Qualified Lead since calculateLifeCycle() checks: if (firstAppointmentMadeDate) → NPS
         */
        payload.firstAppointmentMadeDate = null
      }
      delete payload.contactScheduledDate
    }

    debug(`contacts`, `actions - markPatient payload`, payload)
    handleMarkPatient({
      lifecycleStage: payload.lifecycleStage,
      leadStatus: payload.leadStatus,
      patientStatus: payload.patientStatus,
      productionLtv: payload.productionLtv,
      scheduledProduction: payload.scheduledProduction,
      becamePatientDate:
        payload.becamePatientDate instanceof Date ? payload.becamePatientDate.toISOString() : payload.becamePatientDate,
      becameLeadDate:
        payload.becameLeadDate instanceof Date ? payload.becameLeadDate.toISOString() : payload.becameLeadDate,
      firstAppointmentMadeDate:
        payload.firstAppointmentMadeDate instanceof Date
          ? payload.firstAppointmentMadeDate.toISOString()
          : payload.firstAppointmentMadeDate,
    })
    closeModal()
  }
  return (
    <div className="p-4">
      <SmartForm<ContactData>
        id="updatePatient"
        className="relative h-[700px] w-full content-start md:w-[600px]"
        hiddenInputs={[{ name: `id`, value: contact.id }]}
        defaultValues={contactData}
        validator={markPatientValidator}
        hideButtons
        preventSubmit
      >
        {markPatient ? (
          <>
            <FormHiddenInput name="lifecycleStage" value="Patient" />
            <FormHiddenInput name="leadStatus" value="Closed Won" />
          </>
        ) : (
          <>
            <FormSelect
              cols={6}
              name="lifecycleStage"
              label="Lifecycle Stage"
              options={LIFECYCLE_OPTIONS_FROM_PATIENT}
              value={selectedLifecycle}
              onChange={handleLifecycleChange}
            />
            <FormSelect
              cols={6}
              name="leadStatus"
              label="Lead Status"
              options={getLeadStatusOptions()}
              value={selectedLeadStatus}
              onChange={handleLeadStatusChange}
            />
          </>
        )}
        {isPatientLifecycle && (
          <>
            <FormDatePicker
              cols={6}
              allowFutureDates={false}
              name="becamePatientDate"
              label="Became Patient"
              value={contact.becamePatientDate}
            />
            <FormHiddenInput cols={6} name="firstAppointmentMadeDate" value={new Date().toISOString()} />
            <FormInput cols={6} name="productionLtv" label="Net Production" onBlur={handleFormatCurrency} />
            <FormInput cols={6} name="scheduledProduction" label="Scheduled Production" onBlur={handleFormatCurrency} />
          </>
        )}
        {showScheduledDate && (
          <FormDatePicker
            cols={6}
            allowFutureDates={false}
            name="contactScheduledDate"
            label="Contact Scheduled Date"
          />
        )}
        <div className="absolute bottom-0 left-0 right-0 z-10 bg-surface">
          <div className="mt-4 flex justify-end gap-4">
            <Button label="Cancel" mode="white" onClick={closeModal} disabled={loading} />
            <Button label="Save" mode="primary" onClick={handleSubmitForm} loading={loading} />
          </div>
        </div>
      </SmartForm>
    </div>
  )
}

export function MarkPatientModal({
  children,
  className,
  contact,
  markPatient,
  handleMarkPatient,
}: {
  children: React.ReactNode
  className?: string
  contact: ContactJson
  markPatient?: boolean
  handleMarkPatient: (contact: UpdateContactInput) => void
}): ReactElement {
  const openItPlease = () => {
    debug(`markPatient`, `openItPlease`)
    showModal(<UpdateContact contact={contact} handleMarkPatient={handleMarkPatient} markPatient={markPatient} />, {
      title: markPatient ? `Mark as Patient` : `Update Patient Data`,
      isFullScreen: false,
      useRadix: true,
    })
  }
  return (
    <div className={classNames(className, `w-100 cursor-pointer`)} onClick={openItPlease}>
      {children}
    </div>
  )
}
